









































































































import { AxiosError } from 'axios';
import { createNamespacedHelpers, useStore } from 'vuex-composition-helpers';
import {
  defineComponent,
  ref,
  computed,
  PropType,
  toRefs,
  watch,
  provide
} from '@vue/composition-api';
import {
  IWorkflowFormTemplateRevision,
  IWorkflowFormTemplateRevisionAccessCondition
} from '@/models/workflow-form-template/workflow-form-template.types';

import { ISystemError } from '@/models';
import { setErrorFromAxiosError } from '@/utils/helpers';

import { IRootState } from '@/store';
import {
  IWorkflowFormTemplateState,
  IWorkflowFormTemplateRevisionGetters,
  IWorkflowFormTemplateRevisionActions,
  IWorkflowFormTemplateRevisionMutations
} from '@/store/workflow-form-template';
import { SelectedWorkflowFormFieldKey } from './tabs/WorkflowFormBuilderCalculationTab.vue';

import ALDialog from '@/components/core/Dialog/ALDialog.vue';
import ErrorAlert from '@/components/common/ErrorAlert.vue';
import DeleteConfirmationDialog from '@/components/common/DeleteConfirmationDialog.vue';
import DetailOptionsButton from '@/components/common/DetailOptionsButton.vue';
import WorkflowFormBuilderRevisionGrantAccessFormDialog from './WorkflowFormBuilderRevisionGrantAccessFormDialog.vue';
import WorkflowFormBuilderRevisionGrantAccessDetailOptionButton from './WorkflowFormBuilderRevisionGrantAccessDetailOptionButton.vue';

export default defineComponent({
  name: 'WorkflowFormBuilderRevisionGrantAccessCardDialog',
  components: {
    ALDialog,
    ErrorAlert,
    DeleteConfirmationDialog,
    DetailOptionsButton,
    WorkflowFormBuilderRevisionGrantAccessFormDialog,
    WorkflowFormBuilderRevisionGrantAccessDetailOptionButton
  },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    revision: {
      type: Object as PropType<IWorkflowFormTemplateRevision>,
      required: true
    },
    disabled: {
      type: Boolean,
      default: false
    },

    templateId: {
      type: Number,
      required: true
    },

    revisionId: {
      type: Number,
      required: true
    }
  },

  emits: ['update:dialog'],

  setup(props, { emit }) {
    const localDialog = computed({
      get: () => props.dialog,
      set: value => {
        emit('update:dialog', value);
      }
    });

    const { revision } = toRefs(props);

    // Note: .value dereference is intentional - WorkflowFormFieldProvide expects MaybeRef<number>, not ComputedRef
    provide(SelectedWorkflowFormFieldKey, {
      revisionId: computed(() => revision.value.id!).value
    });

    watch(
      () => props.revision.access_conditions,
      newVal => {
        accessComputationList.value = newVal ?? [];
      },
      { deep: true }
    );

    const store = useStore<IRootState>();

    const { useActions } = createNamespacedHelpers<
      IWorkflowFormTemplateState,
      IWorkflowFormTemplateRevisionGetters,
      IWorkflowFormTemplateRevisionActions,
      IWorkflowFormTemplateRevisionMutations
    >(store, 'workflowFormTemplate');

    const { updateWorkflowFormTemplateRevision } = useActions([
      'updateWorkflowFormTemplateRevision'
    ]);

    // State for dialogs and edit form
    const accessComputationRuleFormDialog = ref(false);
    const selectedAccessComputationRule = ref<IWorkflowFormTemplateRevisionAccessCondition | null>(
      null
    );

    const accessComputationRuleDeleteDialog = ref(false);
    const isAccessComputationRuleDeleteInProgress = ref(false);
    const accessComputationRuleDeleteError = ref<ISystemError | null>(null);

    function cancelDeleteAccessComputationRuleDialog() {
      accessComputationRuleDeleteDialog.value = false;
    }

    function removeAccessComputationRule() {
      isAccessComputationRuleDeleteInProgress.value = true;
      accessComputationRuleDeleteError.value = null;

      const updatedList = accessComputationList.value.filter(
        computation =>
          computation.id !== selectedAccessComputationRule.value!.id
      );
      updateWorkflowFormTemplateRevision({
        templateId: props.templateId,
        revision: {
          id: props.revisionId,
          access_conditions: updatedList
        }
      })
        .then(() => {
          emit('refreshRevision');
          accessComputationRuleDeleteDialog.value = false;
        })
        .catch((err: AxiosError) => {
          accessComputationRuleDeleteError.value = setErrorFromAxiosError(err);
        })
        .finally(() => {
          isAccessComputationRuleDeleteInProgress.value = false;
        });
    }

    const accessComputationList = computed<
      IWorkflowFormTemplateRevisionAccessCondition[]
    >({
      get() {
        return revision.value?.access_conditions ?? [];
      },

      set(value: IWorkflowFormTemplateRevisionAccessCondition[]) {
        revision.value!.access_conditions = value;
      }
    });

    // Note: Grant access rules are intentionally editable for published revisions (security/permissions, not structure)
    function addGrantAccessRule() {
      openRuleFormDialog();
    }

    function editGrantAccessRule(
      accessComputationRule: IWorkflowFormTemplateRevisionAccessCondition
    ) {
      openRuleFormDialog(accessComputationRule);
    }

    function openRuleFormDialog(
      accessComputationRule?: IWorkflowFormTemplateRevisionAccessCondition
    ) {
      accessComputationRuleFormDialog.value = true;
      selectedAccessComputationRule.value = accessComputationRule ?? null;
    }

    function deleteGrantAccessRule(
      accessComputationRule: IWorkflowFormTemplateRevisionAccessCondition
    ) {
      accessComputationRuleDeleteDialog.value = true;
      selectedAccessComputationRule.value = accessComputationRule;
    }

    function refreshRevision() {
      emit('refreshRevision');
    }

    function clearAllErrors() {
      accessComputationRuleDeleteError.value = null;
    }

    function closeDialog() {
      localDialog.value = false;
    }

    function openDialog() {
      localDialog.value = true;
    }

    return {
      localDialog,
      openDialog,
      closeDialog,
      accessComputationRuleFormDialog,
      selectedAccessComputationRule,

      accessComputationList,
      refreshRevision,

      addGrantAccessRule,
      editGrantAccessRule,
      deleteGrantAccessRule,

      accessComputationRuleDeleteDialog,
      isAccessComputationRuleDeleteInProgress,
      accessComputationRuleDeleteError,
      cancelDeleteAccessComputationRuleDialog,
      removeAccessComputationRule,
      clearAllErrors
    };
  }
});
